import { RecordStore } from '@deqo/foundation';
import { Component, ComponentDescriptor, ComponentType } from '../../concepts/objects';
import { Point } from '../../platform/geom/Point';
import { Rect } from '../../platform/geom/Rect';
import { Physical } from './entities/Physical';
import { Snappable } from './entities/Snappable';

export type GridModeOption = 'dots-lines' | 'dots' | 'off';
export type GridSubdivisionOption = 'base' | 'dynamic' | 'off';
export type GridSnappingOption = 'classic' | 'dynamic';

const initialState = {
    enabled: false,
    mode: 'dots' as GridModeOption,
    lines: 400,
    gridSnap: true,
    guideSnap: true,
};

const normalize = (d: number) => {
    d = Math.round(d / 10) * 10;
    d = Math.min(10000, d);
    d = Math.max(10, d);
    return d;
};

export class GridSystem extends RecordStore(initialState) {

    constructor() {
        super();
        this.hook(s => {
            s.lines = normalize(s.lines);
        });
    }

    public get size() {
        return 10;
    }

    public configure(patch: Partial<typeof initialState>) {
        this.updateState(patch);
    }

    public reset() {
        this.configure({ ...initialState, enabled: this.enabled });
    }

    public snap(ptOrRect: Point): Point;
    public snap(ptOrRect: Rect): Rect;
    public snap(ptOrRect: Point | Rect) {
        const { size } = this;
        if (ptOrRect instanceof Rect) {
            return new Rect(
                Math.round(ptOrRect.left / size) * size,
                Math.round(ptOrRect.top / size) * size,
                Math.round(ptOrRect.width / size) * size,
                Math.round(ptOrRect.height / size) * size,
            );
        }
        else {
            return new Point(
                Math.round(ptOrRect.x / size) * size,
                Math.round(ptOrRect.y / size) * size,
            );
        }
    }

    public shouldSnap<T extends Physical>(input: string | ComponentDescriptor | ComponentType<T> | T | Iterable<T>) {
        if (typeof (input) === 'string') {
            return this.shouldSnap(Component.describe(input));
        }
        if (typeof (input) === 'function') {
            return this.shouldSnap(Component.describe(input));
        }
        if (input instanceof Component && input.is(Physical)) {
            if (input.is(Snappable)) {
                return input.as(Snappable).shouldSnap;
            }
            return this.shouldSnap(input.type);
        }
        if ('implements' in input) {
            return input.implements.includes(Snappable);
        }
        if (Array.isArray(input)) {
            return !!input.find(x => this.shouldSnap(x));
        }
        return false;
    }
}
